import type { HeroId } from '../core/types';

/** Lines under the sign while the glen finishes waking. */
export const GLEN_TIPS = [
  'Matching four gives a bonus turn!',
  'Three of a kind clears. Four makes a special.',
  'Hold the chest until it pops, and the store opens.',
  'A win streak fills the vine through the week.',
  'Four rounds. Five moves. Highest score takes the glen.',
  'Dandelion seeds mean the wind is on your side.',
] as const;

/** One critter per visit. The medallion never swaps mid-load. */
export const LOADER_CAST: readonly HeroId[] = [
  'pip',
  'bruno',
  'tuck',
  'bramble',
  'thistle',
  'marlow',
  'cleo',
  'wren',
  'juniper',
  'otto',
  'lumi',
  'barley',
  'fern',
  'posy',
  'dash',
];

/**
 * Largest step the honey fill may take in one frame.
 * A stalled frame cannot snap the bar. The portrait and the sign do not use this.
 */
export const PROGRESS_STEP_CAP = 0.035;

/**
 * One full medallion turn. The spin is a clock, not a progress meter.
 * Reduced motion still turns, slowly, so a stall does not look frozen.
 */
export const PORTRAIT_SPIN_MS = 2400;
export const PORTRAIT_SPIN_REDUCED_MS = 7200;

/** One left-to-right rope sway. It loops for the whole visit. */
export const SIGN_SWAY_MS = 2200;
export const SIGN_SWAY_DEG = 6;
export const SIGN_SWAY_REDUCED_DEG = 2;

/** A decode frame longer than this is not allowed to whip the loops. */
export const LOADER_MOTION_CAP_MS = 50;

/** Cream Fredoka on the wood, large enough to read on a 390-wide phone. */
export const LOADER_PERCENT_SIZE = '40px';

/** Far-layer drift, in world pixels. The backdrop pad stays larger than either axis. */
export const LOADER_FAR_DRIFT_X = 12;
export const LOADER_FAR_DRIFT_Y = 6;
export const LOADER_BACKDROP_PAD = LOADER_FAR_DRIFT_X + LOADER_FAR_DRIFT_Y;

/** How long the ropes swing in before the sign may leave. */
export const LOADER_ENTRANCE_SEC = 1.05;

export function glenProgress(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.min(1, Math.max(0, amount));
}

/**
 * Ease `shown` toward `target`. The fill never jumps and never runs backward,
 * even when a load event skips ahead or a frame arrives late.
 */
export function smoothGlenProgress(shown: number, target: number, dtSec: number): number {
  const from = glenProgress(shown);
  const goal = Math.max(from, glenProgress(target));
  if (goal - from <= 0.0005) return goal;
  const dt = Number.isFinite(dtSec) ? Math.min(0.05, Math.max(0, dtSec)) : 0;
  if (dt <= 0) return from;
  const step = Math.min((goal - from) * (1 - Math.exp(-3.4 * dt)), PROGRESS_STEP_CAP);
  const next = from + step;
  return goal - next <= 0.0015 ? goal : next;
}

/** Medallion rotation from elapsed time. It keeps turning past a full circle. */
export function portraitSpinAngle(elapsedMs: number, reduced = false): number {
  const period = reduced ? PORTRAIT_SPIN_REDUCED_MS : PORTRAIT_SPIN_MS;
  const t = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  return (t / period) * 360;
}

/** Rope sway from elapsed time. The amplitude does not decay when loading stalls. */
export function signSwayAngle(elapsedMs: number, reduced = false): number {
  const t = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  const amp = reduced ? SIGN_SWAY_REDUCED_DEG : SIGN_SWAY_DEG;
  return Math.sin((t / SIGN_SWAY_MS) * Math.PI * 2) * amp;
}

/** Portrait and sign poses for one moment. Progress is not an input. */
export function loaderMotion(elapsedMs: number, reduced = false): { portrait: number; sign: number } {
  return {
    portrait: portraitSpinAngle(elapsedMs, reduced),
    sign: signSwayAngle(elapsedMs, reduced),
  };
}

/**
 * Scale a game-frame delta so a long decode cannot skip the loops ahead.
 * A normal frame stays at 1. A stalled frame advances at most `LOADER_MOTION_CAP_MS`.
 */
export function loaderMotionScale(deltaMs: number): number {
  const raw = Number.isFinite(deltaMs) ? Math.max(0, deltaMs) : 0;
  if (raw === 0) return 1;
  return Math.min(LOADER_MOTION_CAP_MS, raw) / raw;
}

/** Whole percent for the honey track, matching the fill the player can see. */
export function progressPercentLabel(amount: number): string {
  return `${Math.round(glenProgress(amount) * 100)}%`;
}

export function pickLoaderCritter(random: () => number = Math.random): HeroId {
  const roll = random();
  const span = LOADER_CAST.length;
  if (!Number.isFinite(roll) || span === 0) return 'pip';
  const index = Math.min(span - 1, Math.max(0, Math.floor(roll * span)));
  return LOADER_CAST[index] ?? 'pip';
}

let chosen: HeroId | null = null;

/** The critter for this visit. Stable until `beginLoaderCritter`. */
export function loaderCritter(): HeroId {
  if (!chosen) chosen = pickLoaderCritter();
  return chosen;
}

/** Pick again. Called once each time the gate opens. */
export function beginLoaderCritter(random: () => number = Math.random): HeroId {
  chosen = pickLoaderCritter(random);
  return chosen;
}

export function entranceSettled(ageSec: number, reduced: boolean): boolean {
  return ageSec >= (reduced ? 0.16 : LOADER_ENTRANCE_SEC);
}

/**
 * Far painting in world space. The camera is scrolled so the layout sits inside
 * the extra pixels; this frame covers that whole view, plus room to drift,
 * so a phone does not show a hard edge of the sky.
 */
export function loaderBackdrop(
  gameW: number,
  gameH: number,
  layoutW: number,
  layoutH: number,
): { x: number; y: number; w: number; h: number } {
  const width = Number.isFinite(gameW) ? gameW : layoutW;
  const height = Number.isFinite(gameH) ? gameH : layoutH;
  const bleedX = Math.max(0, (width - layoutW) / 2);
  const bleedY = Math.max(0, (height - layoutH) / 2);
  const pad = LOADER_BACKDROP_PAD;
  return {
    x: -bleedX + width / 2,
    y: -bleedY + height / 2,
    w: width + pad * 2,
    h: height + pad * 2,
  };
}

/** Offsets from the top of the carved plaque. The portrait sits above the honey track. */
export function loaderStack(): {
  frameSize: number;
  title: number;
  portrait: number;
  tip: number;
  track: number;
  percent: number;
  plankH: number;
} {
  const frameSize = 196;
  const title = 82;
  const portrait = 230;
  const tip = portrait + frameSize / 2 + 24;
  const track = tip + 64;
  const percent = track + 64;
  const plankH = percent + 56;
  return { frameSize, title, portrait, tip, track, percent, plankH };
}
